import { ApiError } from '../../../common/utils/api-utils';
import { ErrorCode, getApiErrorCode } from '../../../common/utils/error-code-utils';
import { RedeemUpsellOfferError, RedeemUpsellOfferParams } from '../interfaces/sharpen-upsell.interface';
import { StoreState } from '../../../store/store.interface';
import { addToLoggedInCartMagentoApiCall } from '../../cart/shop-cart/api/shop-cart.api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchUserProfile } from '../../user-profile/user-profile.thunks';
import { getCustomerToken, loadCustomer } from '../../customer/customer';
import { getLoggedInCartQuoteId } from '../../cart/shop-cart/store/shop-cart.selectors';
import { getLoggedInCartQuoteIdMagento } from '../../cart/shop-cart/thunks/get-loggedin-cart.thunk';
import { getPromoCodeDiscountMagentoApiCall } from '../../promo-code/api/promo-code.apis';
import { invalidateFreightResponse, updateSkipFreightCalculation } from '../../checkout/shop-checkout';
import { isLoggedOutUser } from '../../user-profile/user-profile.selectors';

/**
 * Orchestrating thunk that adds a Sharpen upsell product to the cart
 * and then applies the promo coupon code in a single atomic flow.
 *
 * If the product is already in the cart, the add-to-cart
 * error is ignored and the flow continues so the promo code is still
 * applied and the caller can redirect to the cart.
 */
export const redeemUpsellOffer = createAsyncThunk(
  'upsell/redeemUpsellOffer',
  async ({ shopCartUrlContext, couponCode }: RedeemUpsellOfferParams, thunkAPI) => {
    try {
      thunkAPI.dispatch(invalidateFreightResponse());
      thunkAPI.dispatch(updateSkipFreightCalculation(false));
      await thunkAPI.dispatch(fetchUserProfile({ forceUpdate: true }));

      if (isLoggedOutUser(thunkAPI.getState() as StoreState)) {
        return thunkAPI.rejectWithValue(RedeemUpsellOfferError.NotAuthenticated);
      }

      await thunkAPI.dispatch(loadCustomer());
      await thunkAPI.dispatch(getLoggedInCartQuoteIdMagento(shopCartUrlContext.magentoCheckoutFlow));

      const state = thunkAPI.getState() as StoreState;
      const cartQuoteId = getLoggedInCartQuoteId(state);
      const token = getCustomerToken(state);

      if (!cartQuoteId) {
        return thunkAPI.rejectWithValue(RedeemUpsellOfferError.Generic);
      }

      try {
        await addToLoggedInCartMagentoApiCall(shopCartUrlContext, cartQuoteId, token);
      } catch (error) {
        const apiError = error as ApiError;
        if (getApiErrorCode(apiError) !== ErrorCode.PRODUCT_ALREADY_IN_CART) {
          return thunkAPI.rejectWithValue(RedeemUpsellOfferError.Generic);
        }
      }

      try {
        return await getPromoCodeDiscountMagentoApiCall(couponCode, token, shopCartUrlContext.magentoCheckoutFlow);
      } catch {
        return thunkAPI.rejectWithValue(RedeemUpsellOfferError.PromoFailed);
      }
    } catch {
      return thunkAPI.rejectWithValue(RedeemUpsellOfferError.Generic);
    }
  }
);
